import { alpha } from "@mui/material/styles";

// ---------------------------------------------------------------------------
// Scheme metadata — descriptions + info sections shown to the user
// ---------------------------------------------------------------------------
export const SCHEME_INFO = {
  bank_loan: {
    description:
      "A simple bank loan to finance energy-efficiency projects, repaid through regular debt payments (principal + interest) over a fixed period.",
    lender: "Bank",
    projectSize: "Any",
    goodFor: "Any borrower",
    advantages: [
      "Can have lower interest rates",
      "Wide availability that accommodates any project size",
    ],
    disadvantages: [
      "Requires credit history and checks",
      "Payments are independent of savings actually achieved",
      "Slow approval process",
      "Risk is entirely on the borrower",
    ],
  },
  equity: {
    description:
      "Self-financed equity investment — the project owner funds the project from their own capital with no external repayment obligations.",
    lender: "Self",
    projectSize: "Any",
    goodFor: "Organizations with available capital",
    advantages: [
      "No interest or repayment obligations",
      "Full ownership and control retained",
      "No approval process required",
    ],
    disadvantages: [
      "Requires available capital",
      "Ties up own funds that could be used elsewhere",
    ],
  },
  equity_crowdfunding: {
    description:
      "Raise capital from many small investors through an online platform. Investors receive a share of the project profits generated by energy savings.",
    lender: "Crowd investors via platform",
    projectSize: "Small–medium (€20k–€1M)",
    goodFor: "ESCOs, project developers, startups",
    advantages: [
      "No fixed repayment obligations",
      "Investors share the financial risk of the project",
      "Good option when bank financing is not available",
      "No collateral required",
    ],
    disadvantages: [
      "Profits must be shared with investors",
      "Platform fees can be relatively high",
      "Requires investor interest to successfully raise capital",
      "Interest rates are usually higher than bank loans",
      "More administrative work (reporting to investors)",
    ],
  },
  lending_crowdfunding: {
    description:
      "A traditional loan sourced from many small crowd investors. The borrower repays principal and interest over a fixed period regardless of project performance.",
    lender: "Crowd investors via platform",
    projectSize: "Small–medium (€20k–€1M)",
    goodFor: "SMEs, ESCOs, small project developers",
    advantages: [
      "Easier access to capital than bank loans",
      "Borrower keeps full ownership of the project",
      "Fixed and predictable repayment schedule",
      "Faster financing process",
    ],
    disadvantages: [
      "Interest rates are usually higher than bank loans",
      "Repayment is required even if savings are lower than expected",
      "Platform fees apply",
      "Campaign must attract enough investors",
    ],
  },
  royalty_crowdfunding: {
    description:
      "Investors finance the project in exchange for a percentage of the project's revenue (energy savings) paid as a royalty for a fixed number of years.",
    lender: "Crowd investors via platform",
    projectSize: "Small–medium (€20k–€1M)",
    goodFor: "Startups, ESCOs, small project developers",
    advantages: [
      "No fixed loan repayment required",
      "No equity dilution — investors do not own the project",
      "Payments adjust with project performance",
      "Easier access to capital for new companies",
    ],
    disadvantages: [
      "Investors receive revenue before operating costs",
      "Can reduce long-term project profitability",
      "Platform fees apply",
      "Less common than other crowdfunding models",
    ],
  },
  epc_first_out: {
    description:
      "The ESCO covers the full project cost and recovers its investment from all available energy savings. Once repaid, the client keeps all future savings.",
    lender: "ESCO",
    projectSize: "Medium–large (€50k–€100M)",
    goodFor: "Building owners without capital",
    advantages: [
      "No upfront investment required by the client",
      "ESCO carries most financial risk",
      "Suitable for large efficiency projects",
    ],
    disadvantages: [
      "Client receives no savings during repayment period",
      "ESCO financing can be expensive",
      "Requires reliable measurement of energy savings",
      "Contract negotiation can be complex",
    ],
  },
  epc_shared_savings: {
    description:
      "ESCO finances the project and energy savings are shared between ESCO and client according to an agreed percentage throughout the contract period.",
    lender: "ESCO",
    projectSize: "Medium–large (€50k–€100M)",
    goodFor: "Building owners, municipalities, commercial buildings",
    advantages: [
      "No upfront investment required",
      "Client benefits from savings immediately",
      "Risk is shared between ESCO and client",
      "ESCO has strong incentive to maximise savings",
    ],
    disadvantages: [
      "Client shares savings with ESCO for the entire contract duration",
      "Effective financing cost may be higher than bank loans",
      "Requires measurement and verification of savings",
      "Contracts can be complex",
    ],
  },
  epc_guaranteed_savings: {
    description:
      "Client finances the project (typically via a bank loan) while the ESCO guarantees a minimum level of energy savings, compensating any shortfall.",
    lender: "Bank or external lender",
    projectSize: "Medium–large (€50k–€100M)",
    goodFor: "Municipalities, large companies, organizations with loan access",
    advantages: [
      "Lower financing cost compared to ESCO financing",
      "Client keeps most of the energy savings",
      "Performance guarantee reduces technical risk",
      "Well-suited for public sector projects",
    ],
    disadvantages: [
      "Client must obtain financing independently",
      "Requires detailed measurement and verification",
    ],
  },
  green_bond_loan: {
    description:
      "Green bond proceeds fund the project as a loan with gradual repayment. Principal and interest are paid down year-by-year, reducing refinancing risk.",
    lender: "Institutional investors",
    projectSize: "Large (>€1M)",
    goodFor: "Large companies, municipalities, utilities",
    advantages: [
      "Gradual repayment reduces refinancing risk",
      "Access to large sustainable investment funds",
      "Suitable for large energy efficiency programs",
      "Stable and predictable financing structure",
    ],
    disadvantages: [
      "High issuance and reporting costs",
      "Requires strong credit profile",
      "Complex financial structuring",
    ],
  },
  green_bond_bullet: {
    description:
      "A green bond where the issuer pays regular interest throughout the term, with the full principal repaid in one lump sum at maturity.",
    lender: "Institutional investors",
    projectSize: "Large (>€1M)",
    goodFor: "Large companies, municipalities, utilities",
    advantages: [
      "Access to large capital amounts",
      "Often lower interest rates due to ESG investor demand",
      "Suitable for long-term infrastructure projects",
      "Attracts institutional investors",
    ],
    disadvantages: [
      "High transaction and certification costs",
      "Complex issuance process",
      "Requires strong creditworthiness",
      "Large lump-sum repayment at maturity",
    ],
  },
  on_bill: {
    description:
      "The financing provider covers the upfront investment. The client repays it through a charge added directly to their electricity bill.",
    lender: "Utility or third-party lender",
    projectSize: "Very small–small (€1k–€50k)",
    goodFor: "Households, small businesses",
    advantages: [
      "No upfront investment required",
      "Repayment integrated into electricity bill",
      "Easier access for customers with limited credit",
      "Often designed so savings offset repayments",
    ],
    disadvantages: [
      "Depends on utility programs and local regulation",
      "Limited to smaller projects",
      "Financing cost may vary",
      "Not widely available in all regions",
    ],
  },
  operational_lease: {
    description:
      "A leasing company buys the energy-efficiency equipment and leases it to the client for a fixed period. The client pays regular lease payments but does not own the equipment.",
    lender: "Leasing company",
    projectSize: "Small–medium (€5k–€1M)",
    goodFor: "SMEs, commercial buildings, industrial facilities",
    advantages: [
      "No upfront capital required",
      "Maintenance is often included",
      "Easier technology upgrades at end of lease",
    ],
    disadvantages: [
      "Client does not own the equipment",
      "Total cost may be higher than direct purchase",
      "Contracts may limit equipment modifications",
    ],
  },
};

// ---------------------------------------------------------------------------
// Accordion sections for the scheme picker
// ---------------------------------------------------------------------------
export const SCHEME_SECTIONS = [
  { label: "Bank Loan", schemes: ["bank_loan"] },
  {
    label: "Crowdfunding",
    schemes: [
      "equity_crowdfunding",
      "lending_crowdfunding",
      "royalty_crowdfunding",
    ],
  },
  {
    label: "ESCO / EPC Contracts",
    schemes: ["epc_first_out", "epc_shared_savings", "epc_guaranteed_savings"],
  },
  { label: "Green Bonds", schemes: ["green_bond_loan", "green_bond_bullet"] },
  { label: "On-Bill Financing", schemes: ["on_bill"] },
  { label: "Operational Lease", schemes: ["operational_lease"] },
];

// Default values pre-filled when a scheme is first added
export const SCHEME_DEFAULTS = {
  bank_loan: { loan_amount: 48499, term_years: 10 },
  equity_crowdfunding: { platform_fee_percent: 0.04, crowd_share_percent: 0.4 },
  lending_crowdfunding: {
    platform_fee_percent: 0.04,
    loan_amount: 48499,
    term_years: 10,
    fixed_interest_rate: 0.05,
  },
  royalty_crowdfunding: {
    platform_fee_percent: 0.04,
    loan_amount: 48499,
    term_years: 10,
    fixed_interest_rate: 0.05,
  },
  epc_shared_savings: { esco_fee_percent: 0.3, term_years: 10 },
  epc_guaranteed_savings: { guaranteed_savings_amount: 100, term_years: 10 },
  green_bond_loan: {
    loan_amount: 48499,
    term_years: 10,
    fixed_interest_rate: 0.035,
    maintenance_cost: 100,
  },
  green_bond_bullet: {
    loan_amount: 48499,
    term_years: 10,
    fixed_interest_rate: 0.035,
    maintenance_cost: 100,
  },
  on_bill: { fixed_interest_rate: 0.05, term_years: 10 },
  operational_lease: { lease_payment: 500, term_years: 10 },
};

// Heading colours for scheme info sections
export const INFO_COLORS = {
  lender: "warning.main",
  projectSize: "#0d47a1",
  goodFor: "success.main",
  advantages: "success.dark",
  disadvantages: "error.main",
};

// Default values for the new-building form
export const emptyBuilding = {
  name: "Office Building A",
  country: "GR",
  region: "Attica",
  year_of_construction: 2005,
  floor_area: 500,
  eem_capex: 48500,
  eem_energy_savings: 5737,
  eem_maintenance_cost: 200,
  eem_lifetime: 30,
};

// ---------------------------------------------------------------------------
// Shared sx style helpers
// ---------------------------------------------------------------------------
export const glassGreenButtonSx = (theme) => ({
  color: theme.palette.common.white,
  background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
  border: "1px solid",
  borderColor: alpha(theme.palette.common.white, 0.24),
  borderRadius: "999px",
  px: 3.2,
  py: 1.1,
  fontWeight: 800,
  backdropFilter: "blur(14px)",
  boxShadow: `0 14px 32px ${alpha(theme.palette.success.main, 0.3)}`,
  transition: "transform 180ms ease, box-shadow 180ms ease, filter 180ms ease",
  "&:hover": {
    background: `linear-gradient(135deg, ${theme.palette.success.dark} 0%, ${theme.palette.success.main} 100%)`,
    transform: "translateY(-2px)",
    boxShadow: `0 18px 36px ${alpha(theme.palette.success.main, 0.36)}`,
    filter: "brightness(1.03)",
  },
  "&.Mui-disabled": {
    color: alpha(theme.palette.common.white, 0.8),
    background: alpha(theme.palette.success.main, 0.45),
  },
});

export const glassGhostButtonSx = (theme) => ({
  color: theme.palette.text.primary,
  background: `linear-gradient(135deg, ${alpha(theme.palette.common.white, 0.74)} 0%, ${alpha(
    theme.palette.secondary.main,
    0.12,
  )} 100%)`,
  border: "1px solid",
  borderColor: alpha(theme.palette.common.white, 0.5),
  borderRadius: "999px",
  px: 3.2,
  py: 1.1,
  fontWeight: 800,
  backdropFilter: "blur(14px)",
  boxShadow: `inset 0 1px 0 ${alpha(theme.palette.common.white, 0.62)}, 0 10px 24px ${alpha(
    theme.palette.common.black,
    0.08,
  )}`,
  transition: "transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease",
  "&:hover": {
    transform: "translateY(-2px)",
    borderColor: alpha(theme.palette.success.main, 0.28),
    boxShadow: `inset 0 1px 0 ${alpha(theme.palette.common.white, 0.68)}, 0 14px 28px ${alpha(
      theme.palette.common.black,
      0.12,
    )}`,
  },
});

export const glassPanelSx = (theme) => ({
  borderRadius: 3,
  px: 2,
  py: 1.35,
  border: "1px solid",
  borderColor: alpha(theme.palette.common.white, 0.45),
  background: `linear-gradient(135deg, ${alpha(theme.palette.common.white, 0.72)} 0%, ${alpha(
    theme.palette.success.main,
    0.08,
  )} 100%)`,
  backdropFilter: "blur(16px)",
  boxShadow: `inset 0 1px 0 ${alpha(theme.palette.common.white, 0.58)}, 0 12px 30px ${alpha(
    theme.palette.common.black,
    0.08,
  )}`,
  transition: "transform 220ms ease, box-shadow 220ms ease, border-color 220ms ease",
  "&:hover": {
    transform: "translateY(-4px) scale(1.01)",
    borderColor: alpha(theme.palette.success.main, 0.35),
    boxShadow: `inset 0 1px 0 ${alpha(theme.palette.common.white, 0.62)}, 0 18px 38px ${alpha(
      theme.palette.common.black,
      0.12,
    )}`,
  },
});

export const glassDeleteIconButtonSx = (theme) => ({
  color: "secondary.main",
  bgcolor: alpha(theme.palette.common.white, 0.45),
  border: "1px solid",
  borderColor: alpha(theme.palette.common.white, 0.4),
  backdropFilter: "blur(10px)",
  transition: "background-color 180ms ease, transform 180ms ease",
  "&:hover": {
    bgcolor: alpha(theme.palette.secondary.main, 0.12),
    transform: "translateY(-1px)",
  },
});

export const glassViewIconButtonSx = (theme) => ({
  color: "text.primary",
  bgcolor: alpha(theme.palette.common.white, 0.45),
  border: "1px solid",
  borderColor: alpha(theme.palette.common.white, 0.4),
  backdropFilter: "blur(10px)",
  transition: "background-color 180ms ease, transform 180ms ease",
  "&:hover": {
    bgcolor: alpha(theme.palette.success.main, 0.1),
    transform: "translateY(-1px)",
  },
});

export const glassTitlePillSx = (theme) => ({
  display: "inline-flex",
  alignItems: "center",
  px: 2.4,
  py: 1.05,
  borderRadius: "999px",
  border: "1px solid",
  borderColor: alpha(theme.palette.common.white, 0.55),
  background: `linear-gradient(135deg, ${alpha(theme.palette.common.white, 0.72)} 0%, ${alpha(
    theme.palette.secondary.main,
    0.12,
  )} 100%)`,
  backdropFilter: "blur(16px)",
  boxShadow: `inset 0 1px 0 ${alpha(theme.palette.common.white, 0.65)}, 0 10px 24px ${alpha(
    theme.palette.common.black,
    0.08,
  )}`,
});

export const glassTabPillSx = (active) => (theme) => ({
  display: "inline-flex",
  alignItems: "center",
  px: 2.4,
  py: 1.05,
  borderRadius: "999px",
  border: "1px solid",
  cursor: "pointer",
  borderColor: active
    ? alpha(theme.palette.success.main, 1)
    : alpha(theme.palette.common.black, 0.10),
  background: active
    ? alpha(theme.palette.success.main, 0.09)
    : alpha(theme.palette.common.black, 0.04),
  boxShadow: active
    ? `inset 0 1px 0 ${alpha(theme.palette.common.white, 0.75)}, 0 2px 8px ${alpha(theme.palette.success.main, 0.10)}`
    : `inset 0 1px 0 ${alpha(theme.palette.common.white, 0.65)}`,
  transition: "background 180ms ease, border-color 180ms ease, transform 180ms ease",
  "&:hover": {
    transform: "translateY(-1px)",
    borderColor: active
      ? alpha(theme.palette.success.main, 0.50)
      : alpha(theme.palette.common.black, 0.18),
    background: active
      ? alpha(theme.palette.success.main, 0.12)
      : alpha(theme.palette.common.black, 0.07),
  },
});

// Shared top-level page header pill — "New Experiment", "Experiment History", etc.
// Plain object: uses MUI system shorthand values only (no raw theme access needed).
export const pageHeaderPillSx = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "999px",
  px: 4,
  py: 1.1,
  border: "2px solid",
  borderColor: "secondary.main",
  bgcolor: "common.white",
  boxShadow: "0 6px 14px rgba(0,0,0,0.18)",
  cursor: "default",
  userSelect: "none",
  pointerEvents: "none",
};

// Quiet warm pill — section label with warm tint and subtle pink accent (e.g. "Experiment History")
export const quietWarmPillSx = (theme) => ({
  display: "inline-flex",
  alignItems: "center",
  px: 2.4,
  py: 1.05,
  borderRadius: "999px",
  border: "1px solid",
  borderColor: alpha("#F03157", 0.18),
  background: alpha("#ECC896", 0.22),
  boxShadow: `inset 0 1px 0 ${alpha(theme.palette.common.white, 0.80)}, 0 2px 6px ${alpha(theme.palette.common.black, 0.05)}`,
});

// Quiet green pill — section label with subtle green accent (e.g. "New Building")
export const quietGreenPillSx = (theme) => ({
  display: "inline-flex",
  alignItems: "center",
  px: 2.4,
  py: 1.05,
  borderRadius: "999px",
  border: "1px solid",
  borderColor: alpha(theme.palette.success.main, 1),
  background: alpha(theme.palette.success.main, 0.2),
  boxShadow: `inset 0 1px 0 ${alpha(theme.palette.common.white, 0.80)}, 0 2px 6px ${alpha(theme.palette.common.black, 0.04)}`,
});

// Subdued ghost danger button — secondary/cancel actions (e.g. "Cancel" in building form)
export const cancelButtonSx = (theme) => ({
  color: theme.palette.secondary.main,
  background: alpha(theme.palette.secondary.main, 0.05),
  border: "1px solid",
  borderColor: alpha(theme.palette.secondary.main, 0.22),
  borderRadius: "999px",
  px: 3.2,
  py: 1.1,
  fontWeight: 800,
  transition: "background 180ms ease, border-color 180ms ease, transform 180ms ease",
  "&:hover": {
    background: alpha(theme.palette.secondary.main, 0.10),
    borderColor: alpha(theme.palette.secondary.main, 0.38),
    transform: "translateY(-1px)",
  },
});

// ---------------------------------------------------------------------------
// Utility helpers
// ---------------------------------------------------------------------------
export const extractApiError = (err, fallback) => {
  const data = err?.response?.data;
  const msg =
    data?.error?.message ||
    data?.detail ||
    (typeof data === "string" ? data : null) ||
    err?.message ||
    fallback;
  const details = Array.isArray(data?.error?.details) ? data.error.details : null;
  return { msg, details };
};

export const labelFromKey = (k) =>
  String(k || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

export const coerceNumber = (v, kind) => {
  if (v === "" || v === null || v === undefined) return v;
  const n = Number(v);
  if (Number.isNaN(n)) return v;
  return kind === "integer" ? Math.trunc(n) : n;
};

/**
 * Compare two flat param objects for equality.
 * Tolerates float/int differences (50000.0 === 50000) by converting to Number.
 */
export const paramsEqual = (a, b) => {
  const norm = (obj) => {
    const o = obj || {};
    return Object.keys(o)
      .sort()
      .map((k) => {
        const n = Number(o[k]);
        return `${k}=${Number.isFinite(n) ? n : o[k]}`;
      })
      .join(",");
  };
  return norm(a) === norm(b);
};
